(() => {
  const ELEMENT_NAME = 'dj-size-guide';

  if (customElements.get(ELEMENT_NAME)) return;

  const normalize = (value) =>
    String(value || '')
      .normalize('NFKC')
      .trim()
      .toLocaleLowerCase('sv-SE')
      .replace(/[\u00a0\u202f]/g, ' ')
      .replace(/\s+/g, ' ');

  const parseMeasurement = (rawValue, matchMode = 'range') => {
    const text = normalize(rawValue)
      .replace(/,/g, '.')
      .replace(/[\u2212\u2010-\u2015]/g, '-')
      .replace(/>=/g, '≥')
      .replace(/<=/g, '≤');

    if (!text || /\d\s*[x×]\s*\d/.test(text)) return null;

    const numbers = (text.match(/\d+(?:\.\d+)?/g) || []).map(Number).filter(Number.isFinite);
    if (!numbers.length) return null;

    if (numbers.length >= 2 && (text.includes('-') || /\s+till\s+/.test(text))) {
      const min = Math.min(numbers[0], numbers[1]);
      const max = Math.max(numbers[0], numbers[1]);
      return { kind: 'range', min, max, minInclusive: true, maxInclusive: true };
    }

    const value = numbers[0];
    if (/upp till|högst|max(?:imalt)?|≤/.test(text)) {
      return { kind: 'range', min: -Infinity, max: value, minInclusive: true, maxInclusive: true };
    }
    if (/under|mindre än|</.test(text)) {
      return { kind: 'range', min: -Infinity, max: value, minInclusive: true, maxInclusive: false };
    }
    if (/från|minst|lägst|≥|\d\s*\+/.test(text)) {
      return { kind: 'range', min: value, max: Infinity, minInclusive: true, maxInclusive: true };
    }
    if (/över|mer än|>/.test(text)) {
      return { kind: 'range', min: value, max: Infinity, minInclusive: false, maxInclusive: true };
    }

    if (matchMode === 'nearest') return { kind: 'target', target: value };
    return { kind: 'exact', target: value };
  };

  const contains = (measurement, value) => {
    const epsilon = 1e-9;
    if (measurement.kind === 'exact') return Math.abs(value - measurement.target) <= epsilon;
    if (measurement.kind !== 'range') return true;

    const aboveMin = measurement.minInclusive
      ? value >= measurement.min - epsilon
      : value > measurement.min + epsilon;
    const belowMax = measurement.maxInclusive
      ? value <= measurement.max + epsilon
      : value < measurement.max - epsilon;
    return aboveMin && belowMax;
  };

  const centeredScore = (measurement, value) => {
    if (measurement.kind === 'target' || measurement.kind === 'exact') {
      const scale = Math.max(Math.abs(measurement.target), 1);
      return ((value - measurement.target) / scale) ** 2;
    }

    if (measurement.kind !== 'range' || !Number.isFinite(measurement.min) || !Number.isFinite(measurement.max)) {
      return Number.POSITIVE_INFINITY;
    }

    const center = (measurement.min + measurement.max) / 2;
    const halfSpan = Math.max((measurement.max - measurement.min) / 2, 0.1);
    return ((value - center) / halfSpan) ** 2;
  };

  class DjSizeGuide extends HTMLElement {
    constructor() {
      super();

      this.trigger = null;
      this.dialog = null;
      this.productRoot = null;
      this.recommender = null;
      this.result = null;
      this.tableRegion = null;
      this.scrollHint = null;
      this.syncFrame = null;
      this.tableScrollFrame = null;
      this.previouslyFocused = null;
      this.recommendedSize = '';
      this.recommendedLabel = '';
      this.busy = false;
      this.uncertain = false;
      this.skipRestoreFocus = false;

      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
      this.onProductChange = this.onProductChange.bind(this);
      this.onVariantEvent = this.onVariantEvent.bind(this);
      this.onRecommenderSubmit = this.onRecommenderSubmit.bind(this);
      this.onRecommenderInput = this.onRecommenderInput.bind(this);
      this.onTableScroll = this.onTableScroll.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
      this.onPurchase = this.onPurchase.bind(this);
      this.onPurchaseVariantChange = this.onPurchaseVariantChange.bind(this);
      this.onDialogCancel = this.onDialogCancel.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-dj-size-guide-open]');
      this.dialog = this.querySelector('[data-dj-size-guide-dialog]');
      this.recommender = this.querySelector('[data-dj-size-guide-recommender]');
      this.result = this.querySelector('[data-dj-size-guide-result]');
      this.tableRegion = this.querySelector('[data-dj-size-guide-table-region]');
      this.scrollHint = this.querySelector('[data-dj-size-guide-scroll-hint]');
      this.action = this.querySelector('[data-dj-size-guide-action]');
      this.purchase = this.querySelector('[data-dj-size-guide-purchase]');
      this.purchaseSummary = this.querySelector('[data-dj-size-guide-purchase-summary]');
      this.purchaseStatus = this.querySelector('[data-dj-size-guide-purchase-status]');
      this.variantSelect = this.querySelector('[data-dj-size-guide-variant]');
      this.variantLabel = this.querySelector('[data-dj-size-guide-variant-label]');
      this.doneLabel = this.action?.textContent.trim() || 'Klart';
      this.sizeIndex = Number(this.dataset.sizeOptionPosition) - 1;
      try {
        this.variantData = JSON.parse(this.querySelector('[data-dj-size-guide-variants]')?.textContent || '{}');
      } catch { this.variantData = {}; }
      this.productRoot =
        this.closest('[data-testid="product-information-details"]') ||
        this.closest('.shopify-section') ||
        this.parentElement;

      if (!(this.trigger instanceof HTMLButtonElement) || !(this.dialog instanceof HTMLDialogElement)) return;

      document.documentElement.classList.add('dj-native-size-guide-active');
      this.trigger.addEventListener('click', this.open);
      this.querySelectorAll('[data-dj-size-guide-close]').forEach((button) =>
        button.addEventListener('click', this.close)
      );
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('close', this.onDialogClose);
      this.dialog.addEventListener('cancel', this.onDialogCancel);
      this.action?.addEventListener('click', this.onPurchase);
      this.variantSelect?.addEventListener('change', this.onPurchaseVariantChange);
      this.recommender?.addEventListener('submit', this.onRecommenderSubmit);
      this.recommender?.addEventListener('input', this.onRecommenderInput);
      this.productRoot?.addEventListener('change', this.onProductChange, true);
      document.addEventListener('variant:update', this.onVariantEvent);
      document.addEventListener('variant:selected', this.onVariantEvent);
      document.addEventListener('variant:change', this.onVariantEvent);
      this.tableRegion?.addEventListener('scroll', this.onTableScroll, { passive: true });
      window.addEventListener('resize', this.onWindowResize, { passive: true });

      this.scheduleSync();
      this.scheduleTableScrollState();
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.open);
      this.querySelectorAll('[data-dj-size-guide-close]').forEach((button) =>
        button.removeEventListener('click', this.close)
      );
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.dialog?.removeEventListener('cancel', this.onDialogCancel);
      this.action?.removeEventListener('click', this.onPurchase);
      this.variantSelect?.removeEventListener('change', this.onPurchaseVariantChange);
      this.recommender?.removeEventListener('submit', this.onRecommenderSubmit);
      this.recommender?.removeEventListener('input', this.onRecommenderInput);
      this.productRoot?.removeEventListener('change', this.onProductChange, true);
      document.removeEventListener('variant:update', this.onVariantEvent);
      document.removeEventListener('variant:selected', this.onVariantEvent);
      document.removeEventListener('variant:change', this.onVariantEvent);
      this.tableRegion?.removeEventListener('scroll', this.onTableScroll);
      window.removeEventListener('resize', this.onWindowResize);
      window.cancelAnimationFrame(this.syncFrame);
      window.cancelAnimationFrame(this.tableScrollFrame);

      if (!document.querySelector(ELEMENT_NAME)) {
        document.documentElement.classList.remove('dj-native-size-guide-active');
        document.documentElement.classList.remove('dj-size-guide-open');
      }
    }

    open() {
      if (!this.dialog || this.dialog.open) return;
      this.previouslyFocused = document.activeElement;
      this.scheduleSync();
      this.dialog.showModal();
      document.documentElement.classList.add('dj-size-guide-open');
      this.dialog.querySelector('[data-dj-size-guide-close]')?.focus({ preventScroll: true });
      this.scheduleTableScrollState();
    }

    close() {
      if (this.busy) return;
      this.dialog?.close();
    }

    onDialogCancel(event) {
      if (this.busy) event.preventDefault();
    }

    onDialogClick(event) {
      if (event.target === this.dialog) this.close();
    }

    onDialogClose() {
      document.documentElement.classList.remove('dj-size-guide-open');
      if (this.skipRestoreFocus) {
        this.skipRestoreFocus = false;
        this.previouslyFocused = null;
        return;
      }
      const focusTarget =
        this.previouslyFocused instanceof HTMLElement && this.previouslyFocused.isConnected
          ? this.previouslyFocused
          : this.trigger;
      focusTarget?.focus({ preventScroll: true });
      this.previouslyFocused = null;
    }

    onTableScroll() {
      this.scheduleTableScrollState();
    }

    onWindowResize() {
      this.scheduleTableScrollState();
    }

    scheduleTableScrollState() {
      window.cancelAnimationFrame(this.tableScrollFrame);
      this.tableScrollFrame = window.requestAnimationFrame(() => this.syncTableScrollState());
    }

    syncTableScrollState() {
      if (!(this.tableRegion instanceof HTMLElement) || !(this.scrollHint instanceof HTMLElement)) return;

      const maxScrollLeft = Math.max(0, this.tableRegion.scrollWidth - this.tableRegion.clientWidth);
      const canScrollRight = maxScrollLeft > 2 && this.tableRegion.scrollLeft < maxScrollLeft - 2;
      this.scrollHint.hidden = !canScrollRight;
    }

    onProductChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
      if (!target.closest('variant-picker') && !String(target.name || '').startsWith('options[')) return;
      this.scheduleSync();
    }

    onVariantEvent(event) {
      if (!this.isConnected) return;
      const eventTarget = event.target;
      if (eventTarget instanceof Node && this.productRoot && !this.productRoot.contains(eventTarget)) return;
      this.scheduleSync();
    }

    onRecommenderInput(event) {
      if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-dj-size-guide-input]')) return;
      event.target.setCustomValidity('');
      this.clearRecommendation();

      if (!(this.recommender instanceof HTMLFormElement)) return;
      const allInputs = [...this.recommender.querySelectorAll('[data-dj-size-guide-input]')].filter(
        (input) => input instanceof HTMLInputElement
      );
      if (!allInputs.length || allInputs.some((input) => String(input.value || '').trim() === '')) return;

      this.updateRecommendation();
    }

    onRecommenderSubmit(event) {
      event.preventDefault();
      this.updateRecommendation({ reportValidity: true });
    }

    updateRecommendation({ reportValidity = false } = {}) {
      if (!(this.recommender instanceof HTMLFormElement)) return;

      const allInputs = [...this.recommender.querySelectorAll('[data-dj-size-guide-input]')];
      let hasInvalidInput = false;
      const inputs = allInputs
        .map((input, index) => {
          if (!(input instanceof HTMLInputElement)) return null;
          input.setCustomValidity('');
          const rawValue = String(input.value || '').trim();
          const value = Number(rawValue.replace(',', '.'));
          const columnIndex = Number(input.dataset.columnIndex);
          const hasValidFormat = /^\d+(?:[,.]\d+)?$/.test(rawValue);
          if (!hasValidFormat || !Number.isFinite(value) || value <= 0 || !Number.isInteger(columnIndex)) {
            hasInvalidInput = true;
            if (reportValidity) input.setCustomValidity('Ange ett numeriskt värde som är större än 0.');
            return null;
          }
          return {
            columnIndex,
            index,
            matchMode: input.dataset.matchMode === 'nearest' ? 'nearest' : 'range',
            value,
          };
        })
        .filter(Boolean);

      if (hasInvalidInput || !inputs.length || inputs.length !== allInputs.length) {
        if (reportValidity) this.recommender.reportValidity();
        return;
      }

      const candidates = [...this.querySelectorAll('[data-dj-size-guide-row]')]
        .map((row, rowIndex) => {
          let isMatch = true;
          const secondaryNearestScores = [];
          let primaryScore = Number.POSITIVE_INFINITY;

          inputs.forEach((input) => {
            if (!isMatch) return;
            const cell = row.querySelector(`[data-column-index="${input.columnIndex}"]`);
            const measurement = parseMeasurement(cell?.dataset.measurementValue, input.matchMode);
            if (!measurement) {
              isMatch = false;
              return;
            }

            if (input.matchMode === 'range') {
              if (!contains(measurement, input.value)) {
                isMatch = false;
                return;
              }
            }

            const score = centeredScore(measurement, input.value);
            if (input.index === 0) primaryScore = score;
            else if (input.matchMode === 'nearest') secondaryNearestScores.push(score);
          });

          if (!isMatch || secondaryNearestScores.some((score) => !Number.isFinite(score))) return null;
          return {
            row,
            order: Number(row.dataset.rowOrder) || rowIndex,
            secondaryNearestScore: secondaryNearestScores.length
              ? secondaryNearestScores.reduce((sum, score) => sum + score, 0) / secondaryNearestScores.length
              : Number.POSITIVE_INFINITY,
            primaryScore,
          };
        })
        .filter(Boolean);

      this.clearRecommendation();
      if (!candidates.length) {
        this.showResult(this.recommender.dataset.noMatch || '', 'error');
        return;
      }

      const tiebreak = this.recommender.dataset.tiebreak || 'closest_primary';
      candidates.sort((a, b) => {
        if (Number.isFinite(a.primaryScore) && Number.isFinite(b.primaryScore)) {
          if (Math.abs(a.primaryScore - b.primaryScore) > 1e-9) return a.primaryScore - b.primaryScore;
        } else if (Number.isFinite(a.primaryScore) !== Number.isFinite(b.primaryScore)) {
          return Number.isFinite(a.primaryScore) ? -1 : 1;
        }

        if (Number.isFinite(a.secondaryNearestScore) && Number.isFinite(b.secondaryNearestScore)) {
          if (Math.abs(a.secondaryNearestScore - b.secondaryNearestScore) > 1e-9) {
            return a.secondaryNearestScore - b.secondaryNearestScore;
          }
        } else if (Number.isFinite(a.secondaryNearestScore) !== Number.isFinite(b.secondaryNearestScore)) {
          return Number.isFinite(a.secondaryNearestScore) ? -1 : 1;
        }

        if (tiebreak === 'larger') return b.order - a.order;
        return a.order - b.order;
      });

      const recommendedRow = candidates[0].row;
      recommendedRow.classList.add('is-recommended');
      const badge = recommendedRow.querySelector('[data-dj-size-guide-recommended-badge]');
      if (badge instanceof HTMLElement) badge.hidden = false;

      const prefix = this.recommender.dataset.successPrefix || 'Rekommenderad storlek:';
      const sizeLabel = recommendedRow.dataset.sizeLabel || recommendedRow.dataset.sizeValue || '';
      this.showResult(prefix, 'success', sizeLabel);
      this.recommendedSize = recommendedRow.dataset.sizeValue || '';
      this.recommendedLabel = sizeLabel;
      this.renderPurchase();
      this.selectRecommendedSize();
    }

    clearRecommendation() {
      this.recommendedSize = '';
      this.recommendedLabel = '';
      this.renderPurchase();
      this.querySelectorAll('[data-dj-size-guide-row].is-recommended').forEach((row) =>
        row.classList.remove('is-recommended')
      );
      this.querySelectorAll('[data-dj-size-guide-recommended-badge]').forEach((badge) => {
        badge.hidden = true;
      });

      if (this.result instanceof HTMLElement) {
        this.result.hidden = true;
        this.result.textContent = '';
        this.result.removeAttribute('data-state');
      }
    }

    showResult(message, state, sizeLabel = '') {
      if (!(this.result instanceof HTMLElement)) return;

      if (state === 'success' && sizeLabel) {
        const size = document.createElement('span');
        size.className = 'dj-size-guide__result-size';
        size.textContent = sizeLabel;
        this.result.replaceChildren(document.createTextNode(`${message} `), size);
      } else {
        this.result.textContent = message;
      }

      this.result.dataset.state = state;
      this.result.hidden = false;
    }

    productForm() {
      return this.productRoot?.querySelector('product-form-component form');
    }

    extraOptions(variant) {
      return (variant?.options || []).filter((_, index) => index !== this.sizeIndex);
    }

    renderPurchase() {
      if (!this.action || !this.purchase || this.busy || this.uncertain) return;
      const showPurchase = Boolean(this.recommendedSize && this.sizeIndex >= 0);
      this.purchase.hidden = !showPurchase;
      this.action.textContent = this.doneLabel;
      this.action.disabled = false;
      this.setPurchaseStatus('');
      if (!showPurchase) return;

      const variants = this.variantData?.variants || [];
      const choices = variants.filter(variant => normalize(variant.options[this.sizeIndex]) === normalize(this.recommendedSize));
      const currentId = this.productForm()?.querySelector('[name="id"]')?.value || this.variantData.selectedId;
      const current = variants.find(variant => String(variant.id) === String(currentId));
      const preferred = this.chosenExtras || this.extraOptions(current);
      const hasExtraOptions = (this.variantData.options || []).length > 1;
      this.variantSelect.replaceChildren();
      this.variantLabel.hidden = !hasExtraOptions;
      const optionTitle = (this.variantData.options || []).filter((_, index) => index !== this.sizeIndex).join(' / ');
      this.querySelector('[data-dj-size-guide-variant-title]').textContent = `Välj ${optionTitle.toLocaleLowerCase('sv-SE')}`;
      const placeholder = new Option('Välj ett alternativ', '');
      placeholder.disabled = true;
      this.variantSelect.add(placeholder);
      choices.forEach(variant => {
        const label = this.extraOptions(variant).join(' / ') || this.recommendedLabel;
        const option = new Option(`${label}${variant.available ? '' : ' – slutsåld'}`, String(variant.id));
        option.disabled = !variant.available;
        this.variantSelect.add(option);
      });
      const match = hasExtraOptions
        ? choices.find(variant => this.extraOptions(variant).every((value, index) => normalize(value) === normalize(preferred[index])))
        : choices[0];
      this.variantSelect.value = match ? String(match.id) : '';
      this.updatePurchaseButton();
      if (!choices.length) this.setPurchaseStatus('Storleken är inte tillgänglig att köpa just nu.');
    }

    onPurchaseVariantChange() {
      const variant = this.selectedPurchaseVariant();
      if (variant) this.chosenExtras = this.extraOptions(variant);
      this.setPurchaseStatus('');
      this.updatePurchaseButton();
    }

    selectedPurchaseVariant() {
      return this.variantData?.variants?.find(variant =>
        String(variant.id) === this.variantSelect?.value &&
        normalize(variant.options[this.sizeIndex]) === normalize(this.recommendedSize)
      );
    }

    updatePurchaseButton() {
      const variant = this.selectedPurchaseVariant();
      const quantity = Math.max(1, Number(variant?.minimum) || 1);
      this.action.textContent = `Lägg storlek ${this.recommendedLabel} i varukorgen`;
      this.action.disabled = !variant?.available;
      this.purchaseSummary.textContent = `Rekommenderad storlek: ${this.recommendedLabel}${variant ? ` · ${quantity} st · ${variant.price}${quantity > 1 ? '/st' : ''}` : ''}`;
      if (variant && !variant.available) this.setPurchaseStatus('Den här kombinationen är slutsåld. Välj ett annat alternativ.');
    }

    setPurchaseStatus(message) {
      if (!this.purchaseStatus) return;
      this.purchaseStatus.textContent = message;
      this.purchaseStatus.hidden = !message;
    }

    setPurchaseBusy(busy) {
      this.busy = busy;
      this.action.disabled = busy;
      this.purchase?.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-dj-size-guide-input], [data-dj-size-guide-variant], [data-dj-size-guide-close], [data-dj-size-guide-recommender] button').forEach(control => { control.disabled = busy; });
      if (busy) this.action.textContent = 'Lägger till…';
      else if (!this.uncertain) this.updatePurchaseButton();
    }

    returnToProductField(field) {
      this.skipRestoreFocus = true;
      this.dialog.close();
      window.requestAnimationFrame(() => {
        field?.scrollIntoView({ block: 'center', behavior: 'auto' });
        field?.focus();
        field?.reportValidity?.();
      });
    }

    onPurchase() {
      if (this.busy) return;
      if (this.uncertain) {
        this.skipRestoreFocus = true;
        this.dialog.close();
        // Read a fresh cart after an ambiguous/partial response, never trust stale drawer HTML.
        window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
        return;
      }
      if (!this.recommendedSize || this.sizeIndex < 0) { this.close(); return; }
      const variant = this.selectedPurchaseVariant();
      if (!variant?.available) return;
      const form = this.productForm();
      const component = form?.closest('product-form-component');
      if (!form || component?.supportsSizeGuidePurchase !== true) {
        this.setPurchaseStatus('Köpknappen laddas fortfarande. Vänta en stund och försök igen.');
        return;
      }
      if (!form.checkValidity()) {
        this.returnToProductField([...form.elements].find(field => field.willValidate && !field.validity.valid));
        return;
      }
      const photo = form.querySelector('dj-product-photo');
      if (photo?.getAttribute('aria-busy') === 'true' || component.djAddPending) {
        this.setPurchaseStatus('Ett köp behandlas redan. Vänta en stund.');
        return;
      }
      if (photo?.validate && !photo.validate()) {
        this.returnToProductField(photo.querySelector('input[type="file"]'));
        return;
      }
      const plan = new FormData(form).get('selling_plan');
      if ((this.variantData.requiresSellingPlan && !plan) || (plan && !variant.sellingPlans.some(id => String(id) === String(plan)))) {
        this.setPurchaseStatus('Välj ett köp- eller prenumerationsalternativ för den här varianten på produktsidan först.');
        return;
      }
      this.setPurchaseStatus('');
      this.setPurchaseBusy(true);
      const request = {
        variantId: variant.id,
        quantity: Math.max(1, Number(variant.minimum) || 1),
        started: false,
        onStart: () => { request.started = true; },
        onSuccess: () => {
          this.setPurchaseBusy(false);
          this.skipRestoreFocus = true;
          this.dialog.close();
        },
        onError: (message, uncertain = false) => {
          this.uncertain = uncertain;
          this.setPurchaseBusy(false);
          this.setPurchaseStatus(message);
          if (uncertain) { this.action.textContent = 'Kontrollera varukorgen'; this.action.disabled = false; }
        },
      };
      form.djSizeGuideRequest = request;
      try {
        // Keep normal form/app validation and all line-item properties, including uploaded files.
        form.requestSubmit();
      } catch {
        request.onError('Köpet kunde inte startas. Försök igen.');
      }
      if (!request.started) {
        if (form.djSizeGuideRequest === request) delete form.djSizeGuideRequest;
        this.setPurchaseBusy(false);
        this.setPurchaseStatus('Kontrollera produktens uppgifter, till exempel gravyr eller bild, innan du lägger till den.');
        const invalid = this.productRoot.querySelector('.gpo-element.has-error input, .gpo-element.has-error textarea, .gpo-element.has-error select');
        if (invalid) this.returnToProductField(invalid);
      }
    }

    scheduleSync() {
      window.cancelAnimationFrame(this.syncFrame);
      this.syncFrame = window.requestAnimationFrame(() => this.syncSelectedRow());
    }

    nativeOptions() {
      const picker = [...(this.productRoot?.querySelectorAll('variant-picker') || [])].find(
        (element) => element.dataset.productId === this.dataset.productId &&
          element.dataset.templateProductMatch === 'true'
      );
      if (!picker) return null;
      const groups = [...picker.querySelectorAll('.variant-picker__form > .variant-option')];
      const controls = groups.map((group) => group.querySelector('select, input[type="radio"]:checked'));
      return { picker, groups, controls, values: controls.map((control) => control?.value) };
    }

    selectRecommendedSize() {
      // Change only the native size control. Its normal change event updates
      // price, media, purchase ID and visible pills without submitting the form.
      if (!this.recommendedSize || this.sizeIndex < 0 || this.busy || this.uncertain) return;
      const state = this.nativeOptions();
      const group = state?.groups[this.sizeIndex];
      if (!group || state.values.length !== this.variantData.options?.length) return;

      const select = group.querySelector('select');
      const choices = [...(select ? select.options : group.querySelectorAll('input[type="radio"]'))];
      const exact = choices.filter((choice) => choice.value === this.recommendedSize);
      const matches = exact.length ? exact : choices.filter(
        (choice) => normalize(choice.value) === normalize(this.recommendedSize)
      );
      if (matches.length !== 1) return;
      const option = matches[0];
      const control = select || option;
      if (control.disabled || option.disabled || !option.dataset.optionValueId) return;
      if (state.values[this.sizeIndex] === option.value) return;

      // No first-available fallback: a sparse matrix must never change colour,
      // model or another choice just to make the recommendation purchasable.
      const candidate = state.values.map((value, index) => index === this.sizeIndex ? option.value : value);
      const variant = this.variantData.variants?.find((item) =>
        item.options.length === candidate.length && item.options.every((value, index) => value === candidate[index])
      );
      if (!variant) return;
      const connectedUrl = option.dataset.connectedProductUrl;
      if (connectedUrl && connectedUrl !== state.picker.dataset.productUrl?.split('?')[0]) return;

      if (select) select.value = option.value;
      else option.checked = true;
      control.dispatchEvent(new Event('change', { bubbles: true }));
      this.scheduleSync();
    }

    selectedSize() {
      const nativeValue = this.nativeOptions()?.values[this.sizeIndex];
      if (nativeValue) return nativeValue;
      const expectedName = normalize(this.dataset.sizeOptionName || 'Storlek');
      const acceptedNames = new Set([expectedName, 'storlek', 'size', 'stl']);
      const controls = [
        ...(this.productRoot?.querySelectorAll('select[name^="options["], input[name^="options["]') || []),
      ];

      const matchingControls = controls.filter((control) => {
        const match = String(control.name || '').match(/^options\[(.+)]$/);
        return match && acceptedNames.has(normalize(match[1]));
      });

      const select = matchingControls.find((control) => control instanceof HTMLSelectElement);
      if (select) return select.value;

      const checked = matchingControls.find(
        (control) => control instanceof HTMLInputElement && control.checked
      );
      return checked?.value || this.dataset.selectedSize || '';
    }

    syncSelectedRow() {
      const selectedValue = normalize(this.selectedSize());
      this.querySelectorAll('[data-dj-size-guide-row]').forEach((row) => {
        const selected = selectedValue !== '' && normalize(row.dataset.sizeValue) === selectedValue;
        row.classList.toggle('is-selected', selected);
        if (selected) row.setAttribute('aria-current', 'true');
        else row.removeAttribute('aria-current');
      });
    }
  }

  customElements.define(ELEMENT_NAME, DjSizeGuide);
})();
